import React, { useState } from 'react';
import { Calculator, Copy, Trash2, Target } from 'lucide-react';

const BreakevenCalculatorTool = () => {
  const [fixedCosts, setFixedCosts] = useState('');
  const [variableCost, setVariableCost] = useState('');
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const fixed = parseFloat(fixedCosts);
    const variable = parseFloat(variableCost);
    const price = parseFloat(pricePerUnit);

    setError('');
    setResult(null);

    if (isNaN(fixed) || isNaN(variable) || isNaN(price) || fixed < 0 || variable < 0 || price <= 0) {
      setError('Please enter valid positive numbers.');
      return;
    }

    const contributionMargin = price - variable;

    if (contributionMargin <= 0) {
      setError('Price per unit must be greater than variable cost per unit to break even.');
      return;
    }

    // Break-even formula: Fixed Costs / (Price - Variable Cost)
    const breakevenUnits = fixed / contributionMargin;
    const breakevenRevenue = breakevenUnits * price;
    const contributionMarginRatio = (contributionMargin / price) * 100;

    setResult({
      fixedCosts: fixed,
      variableCost: variable,
      pricePerUnit: price,
      contributionMargin,
      contributionMarginRatio,
      breakevenUnits,
      breakevenRevenue
    });
  };

  const copyResult = (value) => {
    navigator.clipboard.writeText(Math.ceil(value).toString());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setFixedCosts('');
    setVariableCost('');
    setPricePerUnit('');
    setResult(null);
    setError('');
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatUnits = (value) => {
    return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Fixed Costs ($)
            </label>
            <input
              type="number"
              value={fixedCosts}
              onChange={(e) => setFixedCosts(e.target.value)}
              placeholder="e.g., 50000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="fixed-costs-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Variable Cost per Unit ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={variableCost}
              onChange={(e) => setVariableCost(e.target.value)}
              placeholder="e.g., 25"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="variable-cost-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Price per Unit ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={pricePerUnit}
              onChange={(e) => setPricePerUnit(e.target.value)}
              placeholder="e.g., 50"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="price-per-unit-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-breakeven-btn"
      >
        <Calculator size={18} />
        Calculate Break-even
      </button>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm" data-testid="error-message">
          {error}
        </div>
      )}

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="breakeven-results">
          {/* Main Result */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target size={18} className="text-green-400" />
                <span className="text-sm text-gray-400">Break-even Point</span>
              </div>
              <button
                onClick={() => copyResult(result.breakevenUnits)}
                className={`p-1.5 rounded transition-colors ${
                  copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-breakeven-units"
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="text-3xl font-bold text-green-400" data-testid="breakeven-units-result">
              {formatUnits(Math.ceil(result.breakevenUnits))} units
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {result.breakevenUnits % 1 !== 0 && `(${result.breakevenUnits.toFixed(2)} exact)`}
            </div>
          </div>

          {/* Secondary Results */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Break-even Revenue</div>
              <div className="text-xl font-bold text-blue-400">
                {formatCurrency(result.breakevenRevenue)}
              </div>
            </div>
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Contribution Margin</div>
              <div className="text-xl font-bold text-yellow-400">
                {formatCurrency(result.contributionMargin)} / unit
              </div>
              <div className="text-sm text-gray-500">
                {result.contributionMarginRatio.toFixed(1)}% of price
              </div>
            </div>
          </div>

          {/* Explanation */}
          <div className="p-4 bg-blue-900/20 border border-blue-800/50 rounded-lg">
            <p className="text-sm text-gray-300">
              You need to sell <span className="text-green-400 font-medium">{formatUnits(Math.ceil(result.breakevenUnits))} units</span> at{' '}
              <span className="text-white font-medium">{formatCurrency(result.pricePerUnit)}</span> each to cover your{' '}
              <span className="text-white font-medium">{formatCurrency(result.fixedCosts)}</span> in fixed costs.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Each unit contributes {formatCurrency(result.contributionMargin)} toward fixed costs after covering variable costs.
            </p>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Calculation Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Fixed Costs</span>
                <span className="text-white font-medium">{formatCurrency(result.fixedCosts)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Variable Cost per Unit</span>
                <span className="text-white font-medium">{formatCurrency(result.variableCost)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Price per Unit</span>
                <span className="text-white font-medium">{formatCurrency(result.pricePerUnit)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Contribution Margin</span>
                <span className="text-yellow-400 font-medium">{formatCurrency(result.contributionMargin)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Break-even Units</span>
                <span className="text-green-400 font-medium">{formatUnits(Math.ceil(result.breakevenUnits))}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Break-even Formula</h3>
        <div className="text-sm text-gray-400 font-mono bg-gray-900 p-3 rounded">
          Break-even Units = Fixed Costs / (Price - Variable Cost)
        </div>
        <div className="text-xs text-gray-500 mt-2">
          The difference between price and variable cost is called the "contribution margin"
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Understanding Break-even</h3>
        <div className="text-sm text-gray-400 space-y-2">
          <div><span className="text-white">Fixed Costs:</span> Expenses that don't change with production (rent, salaries, insurance)</div>
          <div><span className="text-white">Variable Costs:</span> Expenses that change per unit (materials, labor, shipping)</div>
          <div><span className="text-white">Contribution Margin:</span> Amount each sale contributes to covering fixed costs</div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Tips for Lowering Break-even</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• Reduce fixed costs where possible</div>
          <div>• Negotiate better variable costs with suppliers</div>
          <div>• Increase prices if market allows</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-breakeven-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default BreakevenCalculatorTool;
